import { FC, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import { Button, Stack, Text, Textarea } from '@eluve/components';
import { SupportedVendors } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import { siloamTypes, sugarTypes } from '@eluve/vendor';

const SILOAM_SAMPLE_CHART_JSON = JSON.stringify(
  [
    {
      organizationId: 2,
      encounterId: 'a1b2c3d4-e5f6-7890-abcd-ef1234567001',
      patientId: 940001,
      admissionId: -8100001,
      subjective_json: JSON.stringify([
        {
          type: 'PATIENT_COMPLAINT',
          remarks:
            'Chief Complaint: Follow-up for diabetes and blood pressure.',
          created_date: '2025-12-20T09:00:00.000',
          modified_date: '2025-12-20T09:30:00.000',
        },
        {
          type: 'ANAMNESIS',
          remarks: 'Feels well overall. Denies chest pain, SOB, dizziness.',
          created_date: '2025-12-20T09:00:00.000',
          modified_date: '2025-12-20T09:30:00.000',
        },
      ]),
      objective_json: JSON.stringify({
        remarks: 'BP 142/86\nWt 94 kg\nA1c 7.4% (prior 7.1)\nLDL 118',
        created_date: '2025-12-20T09:00:00.000',
        modified_date: '2025-12-20T09:30:00.000',
      }),
      assessment_json: JSON.stringify({
        remarks:
          'T2DM — suboptimal control\nHTN — borderline\nHLD — above goal',
        created_date: '2025-12-20T09:00:00.000',
        modified_date: '2025-12-20T09:30:00.000',
      }),
      planning_json: JSON.stringify({
        remarks:
          'Continue metformin, lisinopril, atorvastatin. Reviewed diet/exercise.',
        created_date: '2025-12-20T09:00:00.000',
        modified_date: '2025-12-20T09:30:00.000',
      }),
    },
  ],
  null,
  2,
);

const SUGAR_SAMPLE_CHART_JSON = JSON.stringify(
  [
    {
      resourceType: 'Composition',
      id: 'example-composition-001',
      status: 'final',
      date: '2025-12-20T09:30:00Z',
      title: 'SOAP Note',
      subject: { reference: 'Patient/example-patient-001' },
      encounter: { reference: 'Encounter/example-encounter-001' },
      section: [
        {
          title: 'Subjective',
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '61150-9',
                display: 'Subjective',
              },
            ],
          },
          text: {
            status: 'generated',
            div: '<div>Chief Complaint: Follow-up for diabetes and blood pressure. Feels well overall. Denies chest pain, SOB, dizziness.</div>',
          },
        },
        {
          title: 'Objective',
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '61149-1',
                display: 'Objective',
              },
            ],
          },
          text: {
            status: 'generated',
            div: '<div>BP 142/86, Wt 94 kg, A1c 7.4% (prior 7.1), LDL 118</div>',
          },
        },
        {
          title: 'Assessment',
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '51848-0',
                display: 'Assessment',
              },
            ],
          },
          text: {
            status: 'generated',
            div: '<div>T2DM — suboptimal control. HTN — borderline. HLD — above goal.</div>',
          },
        },
        {
          title: 'Plan',
          code: {
            coding: [
              { system: 'http://loinc.org', code: '51847-2', display: 'Plan' },
            ],
          },
          text: {
            status: 'generated',
            div: '<div>Continue metformin, lisinopril, atorvastatin. Reviewed diet/exercise.</div>',
          },
        },
      ],
    },
  ],
  null,
  2,
);

type VendorChartConfig = {
  schema: z.ZodTypeAny;
  sampleJson: string;
};

const getVendorChartConfig = (vendor?: SupportedVendors): VendorChartConfig => {
  if (vendor === 'SUGAR') {
    return {
      schema: z.array(sugarTypes.fhirCompositionSchema),
      sampleJson: SUGAR_SAMPLE_CHART_JSON,
    };
  }

  return {
    schema: z.array(siloamTypes.LatestSoapNoteSchema),
    sampleJson: SILOAM_SAMPLE_CHART_JSON,
  };
};

export interface CustomChartsInputProps {
  onSubmitCharts: (charts: Record<string, unknown>[]) => Promise<void>;
  isGenerating: boolean;
  vendor?: SupportedVendors;
}

export const CustomChartsInput: FC<CustomChartsInputProps> = ({
  onSubmitCharts,
  isGenerating,
  vendor,
}) => {
  const { t } = useTranslation();
  const { schema, sampleJson } = useMemo(
    () => getVendorChartConfig(vendor),
    [vendor],
  );
  const [jsonInput, setJsonInput] = useState(sampleJson);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);

  const handleJsonChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setJsonInput(value);

      if (!value.trim()) {
        setValidationError(null);
        setIsValid(false);
        return;
      }

      try {
        const parsed = JSON.parse(value);
        const result = schema.safeParse(parsed);
        if (result.success) {
          setValidationError(null);
          setIsValid(true);
        } else {
          const errorDetails = result.error.errors
            .slice(0, 3)
            .map((err: z.ZodIssue) => {
              const path = err.path.length > 0 ? `[${err.path.join('.')}]` : '';
              return `${path} ${err.message}`;
            })
            .join('\n');
          setValidationError(
            errorDetails || t('insightsSettings.customCharts.invalidSchema'),
          );
          setIsValid(false);
        }
      } catch (err) {
        const syntaxError =
          err instanceof SyntaxError
            ? err.message
            : t('insightsSettings.customCharts.invalidJsonSyntax');
        setValidationError(
          t('insightsSettings.customCharts.jsonParseError', {
            error: syntaxError,
          }),
        );
        setIsValid(false);
      }
    },
    [schema, t],
  );

  const handleSubmit = useCallback(async () => {
    if (!isValid || !jsonInput.trim()) return;

    try {
      const parsed = JSON.parse(jsonInput);
      const charts = schema.parse(parsed);
      await onSubmitCharts(charts as Record<string, unknown>[]);
    } catch {
      setValidationError(t('insightsSettings.customCharts.processFailed'));
    }
  }, [isValid, jsonInput, onSubmitCharts, schema, t]);

  return (
    <Stack gap={3} align="stretch">
      <Text size="xs" weight="semibold" color="secondary">
        {t('insightsSettings.customCharts.title')}
      </Text>
      <Text size="xs" color="tertiary">
        {t('insightsSettings.customCharts.description')}
      </Text>
      <Textarea
        placeholder={t('insightsSettings.customCharts.placeholder')}
        value={jsonInput}
        onChange={handleJsonChange}
        className="min-h-[200px] font-mono text-xs"
        disabled={isGenerating}
      />
      {validationError && (
        <Text
          size="xs"
          color="tertiary"
          className="whitespace-pre-wrap text-text-negative-subtle"
        >
          {validationError}
        </Text>
      )}
      {isValid && (
        <Text size="xs" color="tertiary" className="text-text-positive">
          {t('insightsSettings.customCharts.validJson')}
        </Text>
      )}
      <Button
        size="sm"
        disabled={!isValid || isGenerating}
        onClick={handleSubmit}
      >
        {isGenerating
          ? t('insightsSettings.customCharts.generating')
          : t('insightsSettings.customCharts.generate')}
      </Button>
    </Stack>
  );
};
